import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import ApiService from 'services/apiService';
import { StudentDashboardData, StudentFilter } from '../types';

interface StudentDataContextValue {
    // Data
    data: StudentDashboardData | null;
    loading: boolean;
    error: string | null;

    // Filter state
    filter: StudentFilter;
    updateFilter: (updates: Partial<StudentFilter>) => void;
    activeSchoolYear: number | null;
    schoolYears: number[];
}

const StudentDataContext = createContext<StudentDataContextValue | undefined>(undefined);

interface StudentDataProviderProps {
    children: React.ReactNode;
}

export const StudentDataProvider: React.FC<StudentDataProviderProps> = ({ children }) => {
    const [filter, setFilter] = useState<StudentFilter>({
        schoolYear: null,
        programId: null,
    });

    const [data, setData] = useState<StudentDashboardData | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [activeSchoolYear, setActiveSchoolYear] = useState<number | null>(null);


    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await ApiService.home.getStudentCountsByGrade({
                schoolYear: filter.schoolYear ?? undefined,
                programId: filter.programId ?? undefined,
            });

            setData({
                rows: (response.data ?? []).map(item => ({
                    gradeLevelId: item.gradeLevelId ?? 0,
                    gradeLevelName: item.gradeLevelName ?? '',
                    studentCount: item.studentCount ?? 0,
                })),
                total: response.total ?? 0,
                schoolYear: response.schoolYear ?? 0,
                schoolYearDisplay: response.schoolYearDisplay ?? '',
            });

            setActiveSchoolYear(prev => {
                if (prev === null && response.activeSchoolYear) {
                    return response.activeSchoolYear;
                }
                return prev;
            });
        } catch (err: any) {
            setError(err?.message || 'Failed to load student data');
            console.error('Error fetching student counts by grade:', err);
        } finally {
            setLoading(false);
        }
    }, [filter.schoolYear, filter.programId]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const updateFilter = useCallback((updates: Partial<StudentFilter>) => {
        setFilter(prev => ({ ...prev, ...updates }));
    }, []);

    const schoolYears: number[] = activeSchoolYear
        ? Array.from({ length: 6 }, (_, i) => activeSchoolYear - i)
        : [];

    return (
        <StudentDataContext.Provider
            value={{
                data,
                loading,
                error,
                filter,
                updateFilter,
                activeSchoolYear,
                schoolYears,
            }}
        >
            {children}
        </StudentDataContext.Provider>
    );
};

export const useStudentData = (): StudentDataContextValue => {
    const context = useContext(StudentDataContext);
    if (!context) {
        throw new Error('useStudentData must be used within StudentDataProvider');
    }
    return context;
};